import { useState, useEffect, useCallback } from 'react';

const storyChapters = [
  {
    id: 1,
    label: '01 — The Pattern',
    title: 'Cumulative nervous system load',
    content:
      'For years, I worked with capable, high-functioning women seeking solutions for facial changes that felt deeper than aesthetics. Beneath jaw tension, brow compression, persistent dullness, and premature fatigue, the same pattern repeatedly appeared—cumulative nervous system load. It was rarely dramatic. Yet it was structural.\n\nOver time, I began to recognize how sustained responsibility, pressure, and internal drive quietly shape the face. What appeared as a cosmetic concern was often the visible imprint of accumulated stress carried within the nervous system.\n\nI recognized this pattern first in my patients—and eventually in myself.',
  },
  {
    id: 2,
    label: '02 — The Foundation',
    title: 'Western clinical lens',
    content:
      'My professional foundation began in aesthetics and aromatherapy, studying anatomy, facial structure, and refinement through a Western clinical lens. Treatments improved the surface, yet the deeper tension often returned. Gradually it became clear that the face was not simply cosmetic. It was communicative.\nSubtle shifts in tone, holding patterns, and facial structure reflected internal regulation—signals of nervous system load, hormonal fluctuation, circulatory strain, and systemic fatigue.\n\nThe face was not only something to enhance.\nIt was something to interpret.',
  },
  {
    id: 3,
    label: '03 — The Integration',
    title: 'A signal surface',
    content:
      'This realization led me to Traditional Chinese Medicine, where the body is understood not as isolated systems but as an integrated network of regulation and communication.\n\nThrough acupuncture and botanical medicine, I encountered a framework capable of addressing the patterns beneath what could be seen—restoring balance within the nervous system, circulation, and endocrine rhythm.\n\nThe face gradually revealed itself as a signal surface—a place where internal regulation becomes visible.',
  },
  {
    id: 4,
    label: '04 — The Method',
    title: 'Structural recalibration',
    content:
      'From this integration emerged the Vionne Stability Method™—a facial-led clinical approach designed to restore nervous system regulation while refining visible structural tone.\n\nThe method was created for women who value both inner equilibrium and visible refinement—women who approach beauty not only as correction but as a deliberate cultivation of internal balance and lasting vitality.\n\nIt is a practice of thoughtful self-care, sustained attention, and a meaningful investment in long-term stability.\n\nAt its core lies a simple recognition:\n\nBecause true beauty is never something we create.\nIt appears when the body remembers how to live in balance.',
  },
];

const galleryImages = [
  {
    src: "https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/19867027f21f947d63bfa6ad6ea74d36.png",
    alt: "Facial Acupuncture Treatment",
    caption: "Structural Facial Acupuncture",
  },
  {
    src: "https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/73753656f5cdd9a29e5f5f23ed998cdf.png",
    alt: "Botanical Skincare Ingredients",
    caption: "Botanical Neuro-Modulation",
  },
  {
    src: "https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/87088fb90bd196cade0e18adccabeeb7.png",
    alt: "Atelier Treatment Room",
    caption: "The Vionne Atelier",
  },
  {
    src: "https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/6ecb0af945edd339972c0495c04f6d77.png",
    alt: "Practitioner Treatment",
    caption: "Precision & Attentiveness",
  },
];

const credentials = [
  { icon: 'ri-leaf-line', title: 'Certified Aromatherapist', description: 'CFA / IFA / NAHA' },
  { icon: 'ri-nurse-line', title: 'Acupuncture Diploma', description: 'BC Registered Acupuncturist' },
  { icon: 'ri-shield-star-line', title: 'Qi Beauty Practitioner', description: 'Certified Advanced Practitioner' },
  { icon: 'ri-award-line', title: 'CIDESCO Diploma', description: 'Beauty Therapist Diploma' },
  { icon: 'ri-microscope-line', title: 'Medical Aesthetics', description: 'BC Academy Technician Certificate' },
  { icon: 'ri-flashlight-line', title: 'Laser & IPL', description: 'VTCT Level 4 Aesthetician' },
  { icon: 'ri-flower-line', title: 'Facial Acupuncture', description: 'White Lotus Certificate' },
];

const FounderStory = () => {
  const [openChapter, setOpenChapter] = useState<number | null>(1);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const prevSlide = () => {
    goToSlide((currentSlide - 1 + galleryImages.length) % galleryImages.length);
  };

  const nextSlide = () => {
    goToSlide((currentSlide + 1) % galleryImages.length);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="founder-story" className="bg-warmWhite">
      {/* Compact split layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5" style={{ minHeight: undefined }}>
        {/* Left: Image — full width on mobile, 2/5 on desktop */}
        <div className="relative overflow-hidden lg:col-span-2" style={{ minHeight: undefined }}>
          <img
            src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/afcb765b80725a3062b22510e5bb94c2.png"
            alt="RAc. Vionne Hsieh - Founder"
            className="w-full object-cover h-[320px] sm:h-[420px] lg:h-full lg:absolute lg:inset-0"
            style={{ objectPosition: 'center 15%' }}
          />
          <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-transparent to-warmWhite/20"></div>
          <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6">
            <div className="bg-charcoal/65 backdrop-blur-sm px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-white/10">
              <p className="font-display text-xs sm:text-sm font-medium text-white italic">RAc. Vionne Hsieh</p>
              <p className="text-[9px] sm:text-[10px] text-white/50 font-sans tracking-wide mt-0.5">Founder &amp; Lead Practitioner</p>
            </div>
          </div>
        </div>

        {/* Right: Expandable story */}
        <div className="flex flex-col justify-center px-5 sm:px-10 lg:px-14 py-8 sm:py-12 lg:py-14 bg-warmWhite lg:col-span-3">
          <div className="flex items-center gap-2 mb-4 sm:mb-5">
            <div className="w-5 h-[1px] bg-bronze"></div>
            <span className="text-bronze text-[9px] sm:text-[10px] font-sans font-semibold tracking-[0.3em] uppercase">
              Founder Story
            </span>
          </div>

          <h2 className="font-display text-xl sm:text-2xl font-light text-charcoal italic leading-snug mb-5 sm:mb-8">
            Structural recognition<br />
            <span className="text-bronze">before exhaustion</span><br />
            becomes visible.
          </h2>

          {/* Expandable chapters */}
          <div className="space-y-0 border-t border-warmGray/20">
            {storyChapters.map((chapter) => {
              const isOpen = openChapter === chapter.id;
              return (
                <div key={chapter.id} className="border-b border-warmGray/20">
                  <button
                    className="w-full flex items-center justify-between py-3 sm:py-4 text-left group cursor-pointer"
                    onClick={() => setOpenChapter(isOpen ? null : chapter.id)}
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <span className="text-[9px] font-sans font-semibold tracking-[0.25em] text-bronze/70 uppercase whitespace-nowrap">
                        {chapter.label}
                      </span>
                      <span className={`font-display text-[12px] sm:text-[13px] italic transition-colors duration-300 ${isOpen ? 'text-bronze' : 'text-charcoal group-hover:text-bronze/80'}`}>
                        {chapter.title}
                      </span>
                    </div>
                    <div className={`w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
                      <i className={`ri-add-line text-xs sm:text-sm ${isOpen ? 'text-bronze' : 'text-lightGray'}`}></i>
                    </div>
                  </button>

                  <div
                    className="overflow-hidden transition-all duration-500 ease-in-out"
                    style={{ maxHeight: isOpen ? '500px' : '0px', opacity: isOpen ? 1 : 0 }}
                  >
                    <div className="pb-4 sm:pb-5 pr-3 sm:pr-4 space-y-3">
                      {chapter.content.split('\n\n').map((para, i) => (
                        <p key={i} className={`text-[11.5px] sm:text-[12.5px] leading-[1.8] sm:leading-[1.9] font-sans ${para === 'Because true beauty is never something we create.\nIt appears when the body remembers how to live in balance.' ? 'text-charcoal font-semibold' : 'text-textGray font-light'}`}>
                          {para}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 sm:mt-7 pt-4 sm:pt-5 border-t border-warmGray/20">
            <p className="text-[11px] text-charcoal font-sans font-medium">— RAc. Vionne Hsieh</p>
            <p className="text-[9px] sm:text-[10px] text-lightGray font-sans tracking-widest uppercase mt-1">
              Founder &amp; Lead Practitioner · RAc. | CIDESCO | CFA/IFA/NAHA
            </p>
          </div>
        </div>
      </div>

      {/* ── Gallery Slideshow — side by side with Professional Background ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5">
        {/* Left: Professional Background text */}
        <div className="flex flex-col justify-center px-5 sm:px-10 lg:px-14 py-8 sm:py-12 lg:py-14 bg-cream lg:col-span-3">
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <div className="w-4 h-[1px] bg-bronze"></div>
            <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">Professional Background</span>
          </div>
          <h3 className="font-display text-lg sm:text-xl font-light text-charcoal italic mb-3 sm:mb-5 leading-snug">
            Eastern medicine meets aesthetic precision.
          </h3>
          <p className="text-[12px] sm:text-[12.5px] text-textGray leading-[1.8] sm:leading-[1.9] font-sans font-light mb-5 sm:mb-8">
            Vionne is a BC-registered acupuncturist whose work bridges clinical acupuncture, advanced aesthetics, and botanical therapeutics. Her interdisciplinary training integrates internationally recognized standards, including the CIDESCO Beauty Therapy Diploma, IFA/CFA aromatherapy certification, UK VTCT Level 4 Laser &amp; IPL, and advanced Qi Beauty practice.
            <br /><br />
            This diverse clinical foundation shaped the development of the Vionne Stability Method™—an approach designed to bridge the long-standing gap between classical Eastern medicine and contemporary aesthetic care.
            <br /><br />
            At its core lies a simple recognition:<br />
            True beauty is the visible expression of a regulated nervous system.
          </p>

          {/* Professional Formation — integrated */}
          <div className="pt-4 sm:pt-6 border-t border-warmGray/20">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <span className="text-[9px] font-sans font-semibold tracking-[0.3em] text-bronze/70 uppercase">Professional Formation</span>
              <div className="flex-1 h-[1px] bg-warmGray/20"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
              {/* Category 1 */}
              <div className="group">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-warmGray/20 flex-shrink-0 group-hover:border-bronze/40 transition-colors duration-300">
                    <i className="ri-nurse-line text-bronze text-xs"></i>
                  </div>
                  <p className="font-sans text-[10px] sm:text-[10.5px] font-bold text-charcoal tracking-wide uppercase">Clinical Medicine</p>
                </div>
                <div className="pl-9 space-y-1">
                  <p className="text-[10px] sm:text-[10.5px] text-textGray font-sans font-light leading-relaxed">Acupuncture Diploma</p>
                  <p className="text-[10px] sm:text-[10.5px] text-textGray font-sans font-light leading-relaxed">BC Registered Acupuncturist</p>
                </div>
              </div>

              {/* Category 2 */}
              <div className="group">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-warmGray/20 flex-shrink-0 group-hover:border-bronze/40 transition-colors duration-300">
                    <i className="ri-flashlight-line text-bronze text-xs"></i>
                  </div>
                  <p className="font-sans text-[10px] sm:text-[10.5px] font-bold text-charcoal tracking-wide uppercase">Aesthetic &amp; Skin Science</p>
                </div>
                <div className="pl-9 space-y-1">
                  <p className="text-[10px] sm:text-[10.5px] text-textGray font-sans font-light leading-relaxed">CIDESCO Beauty Therapy Diploma</p>
                  <p className="text-[10px] sm:text-[10.5px] text-textGray font-sans font-light leading-relaxed">VTCT Level 4 Laser &amp; IPL</p>
                  <p className="text-[10px] sm:text-[10.5px] text-textGray font-sans font-light leading-relaxed">BC Academy Medical Aesthetics</p>
                </div>
              </div>

              {/* Category 3 */}
              <div className="group">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-warmGray/20 flex-shrink-0 group-hover:border-bronze/40 transition-colors duration-300">
                    <i className="ri-leaf-line text-bronze text-xs"></i>
                  </div>
                  <p className="font-sans text-[10px] sm:text-[10.5px] font-bold text-charcoal tracking-wide uppercase">Botanical &amp; Integrative Therapies</p>
                </div>
                <div className="pl-9 space-y-1">
                  <p className="text-[10px] sm:text-[10.5px] text-textGray font-sans font-light leading-relaxed">IFA / CFA Aromatherapy Certification</p>
                  <p className="text-[10px] sm:text-[10.5px] text-textGray font-sans font-light leading-relaxed">Advanced Qi Beauty Practitioner</p>
                  <p className="text-[10px] sm:text-[10.5px] text-textGray font-sans font-light leading-relaxed">White Lotus Facial Acupuncture</p>
                </div>
              </div>
            </div>

            <div className="mt-5 sm:mt-6 pt-4 border-t border-warmGray/10">
              <p className="text-[10px] sm:text-[10.5px] text-lightGray font-sans font-light italic leading-relaxed">
                Interdisciplinary training integrating clinical acupuncture, advanced aesthetics, and botanical medicine.
              </p>
            </div>
          </div>
        </div>

        {/* Right: Gallery Slideshow */}
        <div className="relative overflow-hidden lg:col-span-2 h-[260px] sm:h-[360px] lg:h-auto" style={{ minHeight: undefined }}>
          {/* Slides */}
          {galleryImages.map((img, index) => (
            <div
              key={index}
              className="absolute inset-0 transition-opacity duration-700 ease-in-out"
              style={{ opacity: index === currentSlide ? 1 : 0, zIndex: index === currentSlide ? 1 : 0 }}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-charcoal/10 to-transparent"></div>
            </div>
          ))}

          {/* Caption */}
          <div className="absolute bottom-8 sm:bottom-12 left-0 right-0 z-10 flex justify-center">
            <div className="text-center">
              <p className="font-display text-white text-sm sm:text-lg italic font-light tracking-wide drop-shadow-md">
                {galleryImages[currentSlide].caption}
              </p>
            </div>
          </div>

          {/* Left Arrow */}
          <button
            onClick={prevSlide}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/35 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-base sm:text-lg"></i>
          </button>

          {/* Right Arrow */}
          <button
            onClick={nextSlide}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/35 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-base sm:text-lg"></i>
          </button>

          {/* Dot Indicators */}
          <div className="absolute bottom-3 sm:bottom-5 left-0 right-0 z-10 flex justify-center gap-1.5 sm:gap-2">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  index === currentSlide
                    ? 'w-5 sm:w-6 h-1.5 bg-white'
                    : 'w-1.5 h-1.5 bg-white/45 hover:bg-white/70'
                }`}
              />
            ))}
          </div>

          {/* Slide counter */}
          <div className="absolute top-4 sm:top-6 right-5 sm:right-8 z-10">
            <span className="text-white/60 text-[10px] sm:text-[11px] font-sans tracking-widest">
              {String(currentSlide + 1).padStart(2, '0')} / {String(galleryImages.length).padStart(2, '0')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderStory;
